/**
 * Clip Thumbnails Hook (RFC-0633 Issue 5 rewrite)
 *
 * Renders the filmstrip behind a video clip as a row of fixed-AR (16:9)
 * tiles. The strip is composed by deterministically computing how many
 * tiles `N` are needed to fill the clip's pixel width and which source
 * timestamps map to each tile, then pulling the rendered <img> data URLs
 * from a per-source-path cache keyed by `roundedSourceTimeMs`.
 *
 * Behavior contract:
 *   - Each tile is fixed-AR 16:9. Source frames are center-cropped to fit
 *     16:9 without resizing the long axis. A 9:16 portrait source becomes
 *     a horizontal band from the vertical middle.
 *   - `N = ceil(clipPixelWidth / thumbWidth)`. Last tile may overflow the
 *     clip body but `overflow: hidden` clips it. No gap, no visible overlap.
 *   - Cache is module-level, keyed by `(sourcePath, sourceTimeMs100)` where
 *     `sourceTimeMs100 = round(sourceTimeMs / 100) * 100`. Adjacent zoom
 *     levels with timestamps within the same 100ms bin reuse the same tile.
 *   - 500ms debounce on zoom / speed / trim changes so we don't extract on
 *     every wheel tick.
 *
 * Persistence: derived state owned by this hook. Not mirrored to YDoc.
 */

import { useEffect, useRef, useState } from 'react'
import type { TimelineClip } from './types'
import { fetchMediaBlob } from '../../../../api/mediaFetch'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'

const thumbLog = createNadDebug('dw:video-editor:thumb-aspect')

/** Target aspect ratio for every tile. */
const TILE_AR = 16 / 9

/** Debounce window (ms) for zoom / speed / trim recomputation. */
const RECOMPUTE_DEBOUNCE_MS = 500

/** Round source-time to this many ms when caching. Smaller = finer cache. */
const CACHE_BIN_MS = 100

/** Cap on the number of extract events we emit per page session to avoid log spam. */
const MAX_EXTRACT_LOG_EVENTS = 5
let extractLogCount = 0

/** Single tile of a clip's filmstrip. */
export interface ClipThumbnailTile {
  /** Source timestamp this tile represents (seconds). */
  sourceTimeSec: number
  /** Cache key bin (ms, rounded). */
  sourceTimeMs: number
  /** Rendered data URL, or null if extraction is still pending. */
  dataUrl: string | null
  /** Pixel width of this tile (derived from `thumbHeight * 16/9`). */
  thumbWidth: number
}

/** Hook options. */
export interface UseClipThumbnailsOptions {
  /** Zoom level in px / second from the timeline. */
  zoomPxPerSec: number
  /** Tile height in px (set by the caller based on track height). */
  thumbHeight: number
}

/** Tiles for one clip. */
export interface UseClipThumbnailsResult {
  tiles: ClipThumbnailTile[]
  thumbWidth: number
}

// ---------------------------------------------------------------------------
// Module-level cache + extraction job queue
// ---------------------------------------------------------------------------

/**
 * `Map<sourcePath, Map<sourceTimeMs, dataUrl>>`. Lives across hook
 * instances + zoom changes so the same source decoded once survives
 * across clips that share it.
 */
const thumbnailCache: Map<string, Map<number, string>> = new Map()

/** Subscribers per (sourcePath) — fired when any tile lands. */
const cacheSubscribers: Map<string, Set<() => void>> = new Map()

function notifyCache(sourcePath: string) {
  const subs = cacheSubscribers.get(sourcePath)
  if (!subs) return
  for (const fn of subs) {
    try {
      fn()
    } catch {
      // ignore subscriber error
    }
  }
}

/** Pending extraction jobs per source path. Sequential per source. */
interface ExtractionJob {
  sourceTimeMs: number
  thumbWidth: number
  thumbHeight: number
}

const extractionQueues: Map<string, ExtractionJob[]> = new Map()
const extractionRunning: Map<string, boolean> = new Map()
const sourceVideoElements: Map<string, HTMLVideoElement> = new Map()
const sourceBlobUrls: Map<string, string> = new Map()
const sourceLoadingPromises: Map<string, Promise<HTMLVideoElement>> = new Map()

async function ensureSourceVideo(projectId: string, sourcePath: string): Promise<HTMLVideoElement> {
  const cacheKey = `${projectId}::${sourcePath}`
  const existing = sourceVideoElements.get(cacheKey)
  if (existing) return existing
  const inflight = sourceLoadingPromises.get(cacheKey)
  if (inflight) return inflight

  const promise = (async () => {
    const response = await fetchMediaBlob(projectId, sourcePath)
    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    sourceBlobUrls.set(cacheKey, blobUrl)

    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve()
      video.onerror = () => reject(new Error(`Failed to load metadata for ${sourcePath}`))
      video.src = blobUrl
    })

    sourceVideoElements.set(cacheKey, video)
    return video
  })()

  sourceLoadingPromises.set(cacheKey, promise)
  try {
    return await promise
  } finally {
    sourceLoadingPromises.delete(cacheKey)
  }
}

/**
 * Compute the source crop rectangle (sx, sy, sw, sh) to take a 16:9
 * region from the source frame without resizing.
 */
function computeCenterCrop(videoWidth: number, videoHeight: number): {
  sx: number
  sy: number
  sw: number
  sh: number
  cropped: 'horizontal' | 'vertical' | 'none'
} {
  if (videoWidth <= 0 || videoHeight <= 0) {
    return { sx: 0, sy: 0, sw: videoWidth, sh: videoHeight, cropped: 'none' }
  }
  const srcAR = videoWidth / videoHeight
  if (Math.abs(srcAR - TILE_AR) < 1e-3) {
    return { sx: 0, sy: 0, sw: videoWidth, sh: videoHeight, cropped: 'none' }
  }
  if (srcAR > TILE_AR) {
    // Wider than 16:9 — crop horizontal margins (letterbox-on-sides).
    const sw = videoHeight * TILE_AR
    const sx = (videoWidth - sw) / 2
    return { sx, sy: 0, sw, sh: videoHeight, cropped: 'horizontal' }
  }
  // Taller than 16:9 (typical 9:16 portrait) — crop vertical margins.
  const sh = videoWidth / TILE_AR
  const sy = (videoHeight - sh) / 2
  return { sx: 0, sy, sw: videoWidth, sh, cropped: 'vertical' }
}

async function processQueueFor(projectId: string, sourcePath: string): Promise<void> {
  if (extractionRunning.get(sourcePath)) return
  const queue = extractionQueues.get(sourcePath)
  if (!queue || queue.length === 0) return

  extractionRunning.set(sourcePath, true)
  try {
    let video: HTMLVideoElement
    try {
      video = await ensureSourceVideo(projectId, sourcePath)
    } catch (error) {
      extractionQueues.delete(sourcePath)
      thumbLog({
        event: 'thumb-source-load-failed',
        sourcePath,
        error: error instanceof Error ? error.message : String(error),
      })
      notifyCache(sourcePath)
      return
    }
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      // Drop the queue; nothing to do.
      extractionQueues.delete(sourcePath)
      return
    }

    while (queue.length > 0) {
      const job = queue.shift()!
      const cache = thumbnailCache.get(sourcePath) ?? new Map<number, string>()
      thumbnailCache.set(sourcePath, cache)
      if (cache.has(job.sourceTimeMs)) continue

      canvas.width = job.thumbWidth
      canvas.height = job.thumbHeight

      const seekTimeSec = Math.max(0, Math.min(video.duration - 0.001, job.sourceTimeMs / 1000))
      await new Promise<void>((resolve) => {
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked)
          resolve()
        }
        video.addEventListener('seeked', onSeeked)
        try {
          video.currentTime = seekTimeSec
        } catch {
          video.removeEventListener('seeked', onSeeked)
          resolve()
        }
        // Safety timeout.
        setTimeout(() => {
          video.removeEventListener('seeked', onSeeked)
          resolve()
        }, 500)
      })

      // Small wait so the frame is decoded before drawImage.
      await new Promise((r) => setTimeout(r, 30))

      const crop = computeCenterCrop(video.videoWidth, video.videoHeight)
      try {
        ctx.drawImage(
          video,
          crop.sx,
          crop.sy,
          crop.sw,
          crop.sh,
          0,
          0,
          job.thumbWidth,
          job.thumbHeight,
        )
      } catch {
        // Decode failure — leave the cell missing; will be retried on a later pass.
        continue
      }
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6)
      cache.set(job.sourceTimeMs, dataUrl)

      if (extractLogCount < MAX_EXTRACT_LOG_EVENTS) {
        extractLogCount += 1
        thumbLog({
          event: 'thumb-extracted',
          sourcePath,
          sourceTimeMs: job.sourceTimeMs,
          sx: Math.round(crop.sx),
          sy: Math.round(crop.sy),
          sw: Math.round(crop.sw),
          sh: Math.round(crop.sh),
          cropped: crop.cropped,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
        })
      }

      notifyCache(sourcePath)
    }
  } finally {
    extractionRunning.set(sourcePath, false)
    // If new jobs landed while we were running, process again.
    const remaining = extractionQueues.get(sourcePath)
    if (remaining && remaining.length > 0) {
      void processQueueFor(projectId, sourcePath)
    }
  }
}

function scheduleExtraction(
  projectId: string,
  sourcePath: string,
  missingTimesMs: number[],
  thumbWidth: number,
  thumbHeight: number,
) {
  if (missingTimesMs.length === 0) return
  let queue = extractionQueues.get(sourcePath)
  if (!queue) {
    queue = []
    extractionQueues.set(sourcePath, queue)
  }
  const queuedSet = new Set(queue.map((j) => j.sourceTimeMs))
  for (const t of missingTimesMs) {
    if (queuedSet.has(t)) continue
    queue.push({ sourceTimeMs: t, thumbWidth, thumbHeight })
  }
  void processQueueFor(projectId, sourcePath)
}

// ---------------------------------------------------------------------------
// Public hooks
// ---------------------------------------------------------------------------

function computeTimestampsMs(
  clip: TimelineClip,
  zoomPxPerSec: number,
  thumbWidth: number,
): { N: number; timesMs: number[]; clipPixelWidth: number } {
  const effectiveDurationSec = Math.max(0, clip.effectiveDuration)
  const clipPixelWidth = effectiveDurationSec * Math.max(1, zoomPxPerSec)
  if (clipPixelWidth <= 0 || thumbWidth <= 0) {
    return { N: 0, timesMs: [], clipPixelWidth: 0 }
  }
  const N = Math.max(1, Math.ceil(clipPixelWidth / thumbWidth))
  // Each tile covers wall-clock seconds = thumbWidth / zoomPxPerSec.
  // The mid-sample maps to source time:
  //   sourceTime = trimStart + (i + 0.5) * tileWallSec * speed
  // Cap at trimEnd boundary.
  const tileWallSec = thumbWidth / Math.max(1, zoomPxPerSec)
  const sourceEnd = clip.trimStart + effectiveDurationSec * Math.max(0.0001, clip.speed)
  const timesMs: number[] = []
  for (let i = 0; i < N; i++) {
    const sourceSec = clip.trimStart + (i + 0.5) * tileWallSec * Math.max(0.0001, clip.speed)
    const clamped = Math.max(clip.trimStart, Math.min(sourceEnd, sourceSec))
    const binned = Math.round((clamped * 1000) / CACHE_BIN_MS) * CACHE_BIN_MS
    timesMs.push(binned)
  }
  return { N, timesMs, clipPixelWidth }
}

/**
 * Subscribe to per-source cache updates so the hook re-renders when new
 * tiles land.
 */
function useCacheVersion(sourcePath: string | null): number {
  const [version, setVersion] = useState(0)
  useEffect(() => {
    if (!sourcePath) return
    let subs = cacheSubscribers.get(sourcePath)
    if (!subs) {
      subs = new Set()
      cacheSubscribers.set(sourcePath, subs)
    }
    const fn = () => setVersion((v) => v + 1)
    subs.add(fn)
    return () => {
      subs?.delete(fn)
    }
  }, [sourcePath])
  return version
}

/**
 * Compute the tile grid for a single clip. Debounces on zoom/speed/trim
 * changes, schedules extraction for cache misses, and returns the latest
 * known tile array (with `null` placeholders for tiles still extracting).
 */
export function useClipThumbnails(
  clip: TimelineClip,
  options: UseClipThumbnailsOptions,
): UseClipThumbnailsResult {
  const { zoomPxPerSec, thumbHeight } = options
  const projectId = useProjectId()
  const thumbWidth = Math.max(8, Math.round(thumbHeight * TILE_AR))
  const cacheVersion = useCacheVersion(clip.sourcePath ?? null)
  const [grid, setGrid] = useState<{ N: number; timesMs: number[] }>({ N: 0, timesMs: [] })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current)
    // Compute immediately on the very first render so the strip isn't blank
    // for 500ms; subsequent changes wait for the debounce window.
    const initialEmpty = grid.N === 0
    const apply = () => {
      const computed = computeTimestampsMs(clip, zoomPxPerSec, thumbWidth)
      setGrid({ N: computed.N, timesMs: computed.timesMs })

      const cache = thumbnailCache.get(clip.sourcePath) ?? new Map<number, string>()
      const missing: number[] = []
      let cached = 0
      for (const t of computed.timesMs) {
        if (cache.has(t)) cached += 1
        else missing.push(t)
      }

      thumbLog({
        event: 'thumb-grid-computed',
        clipId: clip.id,
        N: computed.N,
        thumbWidth,
        thumbHeight,
        sourceDuration: clip.originalDuration,
        effectiveDuration: clip.effectiveDuration,
        zoomPxPerSec,
        speed: clip.speed,
        clipPixelWidth: Math.round(computed.clipPixelWidth),
      })
      thumbLog({
        event: 'thumb-cache-hit-rate',
        clipId: clip.id,
        requested: computed.N,
        cached,
        missed: missing.length,
      })

      if (missing.length > 0 && clip.sourcePath && projectId) {
        scheduleExtraction(projectId, clip.sourcePath, missing, thumbWidth, thumbHeight)
      }
    }
    if (initialEmpty) {
      apply()
    } else {
      debounceRef.current = setTimeout(apply, RECOMPUTE_DEBOUNCE_MS)
    }
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current)
    }
    // We intentionally exclude `grid.N` so the debounce loop doesn't recurse;
    // grid is initialized once and recomputed on the listed dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clip.id,
    clip.sourcePath,
    clip.trimStart,
    clip.trimEnd,
    clip.speed,
    clip.effectiveDuration,
    clip.originalDuration,
    zoomPxPerSec,
    thumbWidth,
    thumbHeight,
    projectId,
  ])

  const tiles: ClipThumbnailTile[] = grid.timesMs.map((tMs) => {
    const cache = thumbnailCache.get(clip.sourcePath ?? '')
    const dataUrl = cache?.get(tMs) ?? null
    return {
      sourceTimeSec: tMs / 1000,
      sourceTimeMs: tMs,
      dataUrl,
      thumbWidth,
    }
  })

  // Touch cacheVersion so React re-renders on cache fill.
  void cacheVersion
  return { tiles, thumbWidth }
}
